import React, { useEffect, useState } from 'react';
import { Paper, Typography, Box, InputLabel, MenuItem, FormControl, Select, TextField, Checkbox, FormControlLabel, Button } from '@mui/material';
import Grid from '@material-ui/core/Grid';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Formik, Field, Form, useFormikContext } from 'formik';
import { FormHelperText } from '@material-ui/core';
import { childInformationValidationSchema } from 'validations/validationSocialService';
import { notification } from 'antd';
import InstructionText from '../shared/InstructionText';

function SocialService({ handleNext, handleBack, socialServiceInformation, onInformationChange }) {
  const appliancesCategories = [
    'TV', 'Refrigerator', 'Aircon', 'Electric Fan', 'Microwave Oven',
    'Laptop', 'Washing Machine', 'Cellphone', 'Printer', 'Motorsiklo',
    'Kotse', 'Tablet', 'Digital Camera'
  ]
  const roomCategories = ["Kusina", "Palikuran", "Kwarto", "Garahe", "Garden"];

  const generateOptions = (selectedValue) => {
    switch (selectedValue) {
      case 'Kusina':
        return [
          'May linya ng tubig',
          'Higit sa isa (hal. sa loob at dirty kitchen)',
          'Naka-tiles (hal. sa loob at dirty kitchen)'
        ];
      case 'Palikuran':
        return [
          'Tabo',
          'De-Shower',
          'May linya ng tubig'
        ];
      case 'Kwarto':
        return [
          'Dalawa o higit pa ang bilang ng kwarto',
          'Kasya ang tatlo sa bawat kwarto'
        ];
      case 'Garahe':
        return [
          'Kasya ang isang sasakyan',
          'Kasya ang motorsiklo'
        ];
      case 'Garden':
        return [
          'Mahigit sa tatlong metro ang haba'
        ];
      default:
        return [];
    }
  };

  const menuItems = {
    yes: [
      "Bayad at may titulo",
      "Bayad pero walang titulo (rights)",
      "Binabayaran pa lamang"
    ],
    no: [
      "Nangungupahan",
      "Nakikitira",
      "Pagmamay-ari ng Gobyerno",
      "Tagapangalaga",
      "Nakatira sa lupa ng iba",
      "Iba pa"
    ]
  };

  const renderMenuItems = (values) =>
    menuItems[values].map((value, index) =>
      <MenuItem key={index} value={value}>{value}</MenuItem>
    );

  const renderGrid = (values, touched, errors) => (
    <Grid item xs={6} md={3}>
      <FormControl fullWidth size="small">
        <Field
          as={Select}
          name="isHouseOwnerDetails"
          helperText={touched.isHouseOwnerDetails && errors.isHouseOwnerDetails}
          error={touched.isHouseOwnerDetails && errors.isHouseOwnerDetails}
        >
          {renderMenuItems(values)}
        </Field>
        <FormHelperText>{touched.isHouseOwnerDetails && errors.isHouseOwnerDetails}</FormHelperText>
      </FormControl>
    </Grid>
  );

  const renderHouseOwnerDetails = (values, touched, errors) => {
    return values === 'yes' || values === 'no' ? renderGrid(values, touched, errors) : null;
  }


  const saveSocialServiceInformation = (values) => {
    onInformationChange(values);
  }

  /**
   * Auto-scroll to first error field component
   * Detects first validation error and scrolls to it smoothly
   * Also shows notification when validation fails
   */
  const AutoScrollToError = () => {
    const { errors, touched, submitCount, isValidating, isSubmitting } = useFormikContext();
    const [lastSubmitCount, setLastSubmitCount] = useState(0);
    const [hasShownNotification, setHasShownNotification] = useState(false);

    useEffect(() => {
      // Track submit attempts
      if (submitCount > lastSubmitCount) {
        setLastSubmitCount(submitCount);
        setHasShownNotification(false);
      }
    }, [submitCount, lastSubmitCount]);

    useEffect(() => {
      // Show notification after validation completes with errors
      if (!isValidating && !isSubmitting && submitCount > 0 && !hasShownNotification) {
        if (errors && Object.keys(errors).length > 0) {
          const hasVisibleErrors = Object.keys(errors).some(key => touched[key]);
          
          if (hasVisibleErrors) {
            setHasShownNotification(true);
            
            // Show notification for incomplete form
            notification.error({
              message: "Form Incomplete",
              description: "Please fill in all required fields and correct any errors before proceeding.",
              duration: 4,
            });

            // Helper function to get nested field name from error path
            const getFirstErrorFieldName = (errors, touched) => {
              // Handle nested errors (like array fields)
              for (const key of Object.keys(errors)) {
                if (touched[key]) {
                  const error = errors[key];
                  
                  // If it's an array of errors
                  if (Array.isArray(error)) {
                    for (let i = 0; i < error.length; i++) {
                      if (error[i] && touched[key] && touched[key][i]) {
                        // Find first nested error field
                        const nestedError = error[i];
                        const nestedTouched = touched[key][i];
                        
                        for (const nestedKey of Object.keys(nestedError)) {
                          if (nestedTouched[nestedKey]) {
                            return `${key}[${i}].${nestedKey}`;
                          }
                        }
                      }
                    }
                  }
                  
                  // If it's a simple error, return the key
                  return key;
                }
              }
              return null;
            };

            const firstErrorFieldName = getFirstErrorFieldName(errors, touched);
            
            if (firstErrorFieldName) {
              // Try to find the field in the DOM with various selectors
              let errorElement = 
                document.querySelector(`[name="${firstErrorFieldName}"]`) ||
                document.querySelector(`input[name="${firstErrorFieldName}"]`) ||
                document.querySelector(`select[name="${firstErrorFieldName}"]`) ||
                document.querySelector(`textarea[name="${firstErrorFieldName}"]`) ||
                document.querySelector(`[id="${firstErrorFieldName}"]`) ||
                document.querySelector(`[data-field="${firstErrorFieldName}"]`);
              
              // If no direct element found, look for the error message itself
              // This handles checkbox/radio groups that don't have name attributes
              if (!errorElement) {
                // Find all FormHelperText elements with error messages
                const allHelperTexts = document.querySelectorAll('.MuiFormHelperText-root');
                for (const helperText of allHelperTexts) {
                  if (helperText.textContent && helperText.textContent.trim() !== '') {
                    // Check if this helper text is showing one of our errors
                    const errorMessage = errors[firstErrorFieldName];
                    if (errorMessage && helperText.textContent.includes(errorMessage)) {
                      errorElement = helperText;
                      break;
                    }
                  }
                }
              }
              
              // Final fallback: any visible error indicator
              if (!errorElement) {
                errorElement = 
                  document.querySelector('.Mui-error') ||
                  document.querySelector('[aria-invalid="true"]') ||
                  document.querySelector('.MuiFormHelperText-root');
              }
              
              if (errorElement) {
                // Scroll to error with smooth behavior - center vertically in viewport
                const elementRect = errorElement.getBoundingClientRect();
                const absoluteElementTop = elementRect.top + window.pageYOffset;
                const middle = absoluteElementTop - (window.innerHeight / 2) + (elementRect.height / 2);
                
                window.scrollTo({ top: middle, behavior: 'smooth' });
                
                // Focus the element after scrolling if it's focusable
                setTimeout(() => {
                  if (errorElement.focus && typeof errorElement.focus === 'function') {
                    errorElement.focus({ preventScroll: true });
                  }
                }, 500);
              }
            }
          }
        }
      }
    }, [isValidating, isSubmitting, errors, touched, submitCount, hasShownNotification]);

    return null;
  };

  return (

    <Formik
      initialValues={socialServiceInformation}
      validationSchema={childInformationValidationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          saveSocialServiceInformation(values);
          await handleNext();
        } catch (error) {
          console.error('Error submitting social service form:', error);
          notification.error({
            message: 'Error',
            description: 'An error occurred while saving. Please try again.',
          });
        } finally {
          setSubmitting(false);
        }
      }}
      enableReinitialize
    >
      {({ errors, touched, values, setFieldValue, isSubmitting }) => (
        <Form>
          <AutoScrollToError />
          <Paper sx={{ mt: 2 }}>
            <Box p={3}>
              <InstructionText />
              <Box>
                <Box mb={3}>
                  <Typography fontWeight="bold" mb={1}>
                    1. Pag-aari ba ang lupang kinatatayuan ng bahay? <span style={{color: 'red'}}>*</span>
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Grid container spacing={2}>
                        <Grid item xs={6} md={3}>
                          <FormControl fullWidth size="small">
                            <Field
                              as={Select}
                              name="isHouseOwner"
                              helperText={touched.isHouseOwner && errors.isHouseOwner}
                              error={touched.isHouseOwner && errors.isHouseOwner}
                              onChange={(event) => {
                                setFieldValue("isHouseOwner", event.target.value);
                                setFieldValue("isHouseOwnerDetails", "");
                                setFieldValue("isHouseOwnerDetailsOthers", "");
                              }}
                            >
                              <MenuItem value="yes">Oo</MenuItem>
                              <MenuItem value="no">Hindi</MenuItem>
                            </Field>
                            <FormHelperText>{touched.isHouseOwner && errors.isHouseOwner}</FormHelperText>
                          </FormControl>
                        </Grid>
                        {renderHouseOwnerDetails(values.isHouseOwner, touched, errors)}
                        {values.isHouseOwnerDetails === 'Iba pa' ?
                          <Grid item xs={12} md={3}>
                            <Field
                              as={TextField}
                              name="isHouseOwnerDetailsOthers"
                              size="small"
                              fullWidth
                              helperText={touched.isHouseOwnerDetailsOthers && errors.isHouseOwnerDetailsOthers}
                              error={errors.isHouseOwnerDetailsOthers && touched.isHouseOwnerDetailsOthers}
                            />
                          </Grid>
                          : ''}
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
                <Box mb={3}>
                  <Typography fontWeight="bold">
                    2. Alin sa mga sumusunod ang meron kayo? <span style={{color: 'red'}}>*</span>
                  </Typography>
                  <Typography variant="body2" color="textSecondary" mb={2}>
                    (Pumili ng tatlo (3) o higit pa kung meron)
                  </Typography>
                  {values.selectRoomPossession.map((select, index) => (
                    <Box mb={1} key={index}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Grid container spacing={2}>
                            <Grid item xs={6} md={6}>
                              <FormControl fullWidth size="small">
                                <Field
                                  as={Select}
                                  name={`selectRoomPossession[${index}]`}
                                  value={select.value}
                                  onChange={(event) => {
                                    const { value } = event.target;
                                    const updatedSelectComponents = [...values.selectRoomPossession];
                                    updatedSelectComponents[index] = {
                                      ...updatedSelectComponents[index],
                                      secondaryOptions: generateOptions(value),
                                      value: value
                                    };
                                    setFieldValue("selectRoomPossession", updatedSelectComponents);
                                  }}
                                  inputProps={{
                                    'aria-required': true,
                                    'aria-label': 'Room Possession',
                                  }}
                                  helperText={touched.selectRoomPossession && touched.selectRoomPossession[index] && touched.selectRoomPossession[index].value && errors.selectRoomPossession && errors.selectRoomPossession[index] && errors.selectRoomPossession[index].value}
                                  error={touched.selectRoomPossession && touched.selectRoomPossession[index] && touched.selectRoomPossession[index].value && errors.selectRoomPossession && errors.selectRoomPossession[index] && errors.selectRoomPossession[index].value}
                                >
                                  {roomCategories.map((item) => (
                                    !values.selectRoomPossession.map(select => select.value).includes(item) || item === select.value ? (
                                      <MenuItem key={item} value={item} data-testid={item}>
                                        {item}
                                      </MenuItem>
                                    ) : null
                                  ))}
                                </Field>
                                <FormHelperText>
                                  {touched.selectRoomPossession && touched.selectRoomPossession[index] && touched.selectRoomPossession[index].value && errors.selectRoomPossession && errors.selectRoomPossession[index] && errors.selectRoomPossession[index].value}
                                </FormHelperText>
                              </FormControl>
                            </Grid>
                            {select.secondaryOptions.length > 0 && (
                              <Grid item xs={6} md={6}>
                                <Box display="flex" alignItems="center">
                                  <FormControl fullWidth size="small">
                                    <Field
                                      as={Select}
                                      name={`selectedSecondaryOption[${index}]`}
                                      value={select.selectedSecondaryOption || ''}
                                      onChange={(event) => {
                                        const { value } = event.target;
                                        const updatedSelectComponents = [...values.selectRoomPossession];
                                        updatedSelectComponents[index] = {
                                          ...updatedSelectComponents[index],
                                          selectedSecondaryOption: value,
                                        };
                                        setFieldValue("selectRoomPossession", updatedSelectComponents);
                                      }}
                                      inputProps={{
                                        'aria-required': true,
                                        'aria-label': 'Room Possession Details',
                                      }}
                                      helperText={touched.selectRoomPossession && touched.selectRoomPossession[index] && errors.selectRoomPossession && errors.selectRoomPossession[index] && errors.selectRoomPossession[index].selectedSecondaryOption}
                                      error={touched.selectRoomPossession && touched.selectRoomPossession[index] && errors.selectRoomPossession && errors.selectRoomPossession[index] && errors.selectRoomPossession[index].selectedSecondaryOption}
                                    >
                                      {select.secondaryOptions.map((option, secIndex) => (
                                        <MenuItem key={secIndex} value={option}>
                                          {option}
                                        </MenuItem>
                                      ))}
                                    </Field>
                                    <FormHelperText>
                                      {touched.selectRoomPossession && touched.selectRoomPossession[index] && errors.selectRoomPossession && errors.selectRoomPossession[index] && errors.selectRoomPossession[index].selectedSecondaryOption}
                                    </FormHelperText>
                                  </FormControl>
                                  {/* Delete button is never rendered for any field in this section */}
                                </Box>
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                  <FormHelperText error={Boolean(errors.selectRoomPossession && touched.selectRoomPossession)} aria-live="polite">
                    {typeof errors.selectRoomPossession === 'string' && touched.selectRoomPossession ? errors.selectRoomPossession : ''}
                  </FormHelperText>
                  <Button
                    variant="contained"
                    onClick={() => {
                      const newField = [...values.selectRoomPossession, { value: '', secondaryOptions: [], selectedSecondaryOption: '' }];
                      setFieldValue("selectRoomPossession", newField);
                    }}
                    aria-label="addRoomPossession"
                  >
                    <AddIcon fontSize="small" aria-label="addRoomPossession" />
                    Magdagdag
                  </Button>
                </Box>
                <Box>
                  <Typography fontWeight="bold">
                    3. Magkano ang pinagkakagastusan ng pamilya sa mga sumusunod sa loob ng isang araw/buwan? <span style={{color: 'red'}}>*</span>
                  </Typography>
                  <Typography variant="body2" color="textSecondary" mb={2}>
                    (Isulat ang halaga sa buong numero. Halimbawa, 5000. Kung walang gastos, isulat ang 0.)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Box mb={2}>
                            <Field
                              as={TextField}
                              name="dailyFood"
                              size="small"
                              label="Pagkain (kada araw)"
                              type="number"
                              variant="outlined"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              fullWidth
                              helperText={touched.dailyFood && errors.dailyFood}
                              error={errors.dailyFood && touched.dailyFood}
                            />
                          </Box>
                          <Box mb={2}>
                            <Field
                              as={TextField}
                              name="monthlyWater"
                              size="small"
                              label="Tubig (kada buwan)"
                              type="number"
                              variant="outlined"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              fullWidth
                              helperText={touched.monthlyWater && errors.monthlyWater}
                              error={errors.monthlyWater && touched.monthlyWater}
                            />
                          </Box>
                          <Box mb={2}>
                            <Field
                              as={TextField}
                              name="monthlyElectricity"
                              size="small"
                              label="Kuryente (kada buwan)"
                              type="number"
                              variant="outlined"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              fullWidth
                              helperText={touched.monthlyElectricity && errors.monthlyElectricity}
                              error={errors.monthlyElectricity && touched.monthlyElectricity}
                            />
                          </Box>
                          <Box mb={2}>
                            <Field
                              as={TextField}
                              name="monthlyMedicine"
                              size="small"
                              label="Gamot at Pagpapagamot (kada buwan) "
                              type="number"
                              variant="outlined"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              fullWidth
                              helperText={touched.monthlyMedicine && errors.monthlyMedicine}
                              error={errors.monthlyMedicine && touched.monthlyMedicine}
                            />
                          </Box>
                          <Box mb={2}>
                            <Field
                              as={TextField}
                              name="monthlyGrocery"
                              size="small"
                              label="Grocery (bukod sa pagkain) (kada buwan)"
                              type="number"
                              variant="outlined"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              fullWidth
                              helperText={touched.monthlyGrocery && errors.monthlyGrocery}
                              error={errors.monthlyGrocery && touched.monthlyGrocery}
                            />
                          </Box>
                          <Box mb={2}>
                            <Field
                              as={TextField}
                              name="monthlyInsurance"
                              size="small"
                              label="Insurance (kada buwan)"
                              type="number"
                              variant="outlined"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              fullWidth
                              helperText={touched.monthlyInsurance && errors.monthlyInsurance}
                              error={errors.monthlyInsurance && touched.monthlyInsurance}
                            />
                          </Box>
                          <Box sx={{ mb: { xs: 0, md: 2 } }}>
                            <Field
                              as={TextField}
                              name="monthlyTransportation"
                              size="small"
                              label="Transportation (kada araw)"
                              type="number"
                              variant="outlined"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              fullWidth
                              helperText={touched.monthlyTransportation && errors.monthlyTransportation}
                              error={errors.monthlyTransportation && touched.monthlyTransportation}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box mb={2}>
                            <Field
                              as={TextField}
                              name="monthlyRent"
                              size="small"
                              label="Upa/Renta (kada buwan)"
                              type="number"
                              variant="outlined"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              fullWidth
                              helperText={touched.monthlyRent && errors.monthlyRent}
                              error={errors.monthlyRent && touched.monthlyRent}
                            />
                          </Box>
                          <Box mb={2}>
                            <Field
                              as={TextField}
                              name="monthlyLoad"
                              size="small"
                              label="Load at Mobile Data (kada buwan)"
                              type="number"
                              variant="outlined"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              fullWidth
                              helperText={touched.monthlyLoad && errors.monthlyLoad}
                              error={errors.monthlyLoad && touched.monthlyLoad}
                            />
                          </Box>
                          <Box mb={2}>
                            <Field
                              as={TextField}
                              name="monthlyAllowance"
                              size="small"
                              label="Edukasyon at Baon (kada buwan)"
                              type="number"
                              variant="outlined"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              fullWidth
                              helperText={touched.monthlyAllowance && errors.monthlyAllowance}
                              error={errors.monthlyAllowance && touched.monthlyAllowance}
                            />
                          </Box>
                          <Box mb={2}>
                            <Field
                              as={TextField}
                              name="monthlyTherapy"
                              size="small"
                              label="Therapy (kada buwan)"
                              type="number"
                              variant="outlined"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              fullWidth
                              helperText={touched.monthlyTherapy && errors.monthlyTherapy}
                              error={errors.monthlyTherapy && touched.monthlyTherapy}
                            />
                          </Box>
                          <Box mb={2}>
                            <Field
                              as={TextField}
                              name="monthlyOnlineShopping"
                              size="small"
                              label="Online shopping (kada buwan)"
                              type="number"
                              variant="outlined"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              fullWidth
                              helperText={touched.monthlyOnlineShopping && errors.monthlyOnlineShopping}
                              error={errors.monthlyOnlineShopping && touched.monthlyOnlineShopping}
                            />
                          </Box>
                          <Box mb={2}>
                            <Field
                              as={TextField}
                              name="monthlyHygiene"
                              size="small"
                              label="Gupit, Manicure, Spa (kada buwan)"
                              type="number"
                              variant="outlined"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              fullWidth
                              helperText={touched.monthlyHygiene && errors.monthlyHygiene}
                              error={errors.monthlyHygiene && touched.monthlyHygiene}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
                <Box mb={2}>
                  <Typography fontWeight="bold">
                    4. Alin sa mga gamit na ito ang meron ang pamilya? Isulat kung ilan at klase nito, sa papaanong paraan ito nakuha, at
                    kung gaano na ito katagal sa ating pagmamay-ari. <span style={{color: 'red'}}>*</span>
                  </Typography>
                  <Typography variant="body2" color="textSecondary" mb={2}>
                    (Pumili ng isa (1) o higit pa kung meron)
                  </Typography>
                  {values.appliancesOwned.map((select, index) => (
                    <Box key={index} mb={2}>
                      <Grid container spacing={2} mb={2} wrap="nowrap">
                        <Grid item xs={11}>
                          <Grid container spacing={2}>
                            <Grid item xs={6} md={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel id={`appliancesOwned[${index}].item`}>Kagamitan</InputLabel>
                                <Field
                                  as={Select}
                                  name={`appliancesOwned[${index}].item`}
                                  label="Kagamitan"
                                  value={select.item}
                                  helperText={touched.appliancesOwned && touched.appliancesOwned[index] && touched.appliancesOwned[index].item && errors.appliancesOwned && errors.appliancesOwned[index] && errors.appliancesOwned[index].item}
                                  error={touched.appliancesOwned && touched.appliancesOwned[index] && touched.appliancesOwned[index].item && errors.appliancesOwned && errors.appliancesOwned[index] && errors.appliancesOwned[index].item}
                                >
                                  {appliancesCategories.map((item) => (
                                    // Only render the MenuItem if the item is not selected in any other Select
                                    !values.appliancesOwned.map(select => select.item).includes(item) || item === select.item ? (
                                      <MenuItem key={item} value={item}>
                                        {item}
                                      </MenuItem>
                                    ) : null
                                  ))}
                                </Field>
                                <FormHelperText>
                                  {touched.appliancesOwned && touched.appliancesOwned[index] && touched.appliancesOwned[index].item && errors.appliancesOwned && errors.appliancesOwned[index] && errors.appliancesOwned[index].item}
                                </FormHelperText>
                              </FormControl>
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <Field
                                as={TextField}
                                name={`appliancesOwned[${index}].type`}
                                size="small"
                                label="Ilan at Anong Klase (Hal. 1, 40 inches Samsung)"
                                value={select.type}
                                variant="outlined"
                                inputProps={{
                                  'aria-label': `appliancesOwnedType${index}`
                                }}
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                fullWidth
                                helperText={touched.appliancesOwned && touched.appliancesOwned[index] && touched.appliancesOwned[index].type && errors.appliancesOwned && errors.appliancesOwned[index] && errors.appliancesOwned[index].type}
                                error={touched.appliancesOwned && touched.appliancesOwned[index] && touched.appliancesOwned[index].type && errors.appliancesOwned && errors.appliancesOwned[index] && errors.appliancesOwned[index].type}
                              />
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel id={`appliancesOwned[${index}].obtained`}>Papaano ito nakuha?</InputLabel>
                                <Field
                                  as={Select}
                                  name={`appliancesOwned[${index}].obtained`}
                                  inputProps={{
                                    'aria-label': `appliancesOwnedObtained${index}`,
                                  }}
                                  label="Papaano ito nakuha?"
                                  value={select.obtained}
                                  helperText={touched.appliancesOwned && touched.appliancesOwned[index] && touched.appliancesOwned[index].obtained && errors.appliancesOwned && errors.appliancesOwned[index] && errors.appliancesOwned[index].obtained}
                                  error={touched.appliancesOwned && touched.appliancesOwned[index] && touched.appliancesOwned[index].obtained && errors.appliancesOwned && errors.appliancesOwned[index] && errors.appliancesOwned[index].obtained}
                                >
                                  <MenuItem value={'Binili at bayad na'}>Binili at bayad na</MenuItem>
                                  <MenuItem value={'Binili at binabayaran pa'}>Binili at binabayaran pa</MenuItem>
                                  <MenuItem value={'Bigay'}>Bigay</MenuItem>
                                </Field>
                                <FormHelperText>
                                  {touched.appliancesOwned && touched.appliancesOwned[index] && touched.appliancesOwned[index].obtained && errors.appliancesOwned && errors.appliancesOwned[index] && errors.appliancesOwned[index].obtained}
                                </FormHelperText>
                              </FormControl>
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <Box display="flex" alignItems="center" justifyContent="center">
                                <Field
                                  as={TextField}
                                  name={`appliancesOwned[${index}].periodOfOwned`}
                                  size="small"
                                  label="Tagal sa pagmamay-ari? (Hal. 6 months)"
                                  value={select.periodOfOwned}
                                  variant="outlined"
                                  inputProps={{
                                    'aria-label': `appliancesOwnedPeriodOfOwned${index}`
                                  }}
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                  fullWidth
                                  helperText={touched.appliancesOwned && touched.appliancesOwned[index] && touched.appliancesOwned[index].periodOfOwned && errors.appliancesOwned && errors.appliancesOwned[index] && errors.appliancesOwned[index].periodOfOwned}
                                  error={touched.appliancesOwned && touched.appliancesOwned[index] && touched.appliancesOwned[index].periodOfOwned && errors.appliancesOwned && errors.appliancesOwned[index] && errors.appliancesOwned[index].periodOfOwned}
                                />
                              </Box>
                            </Grid>
                          </Grid>
                        </Grid>
                        {/* Delete button removed as per requirements */}
                      </Grid>
                    </Box>
                  ))}
                  <Button variant="contained"
                    onClick={() => {
                      const newField = [...values.appliancesOwned, { item: '', type: '', obtained: '', periodOfOwned: '', }];
                      setFieldValue("appliancesOwned", newField);
                    }}
                    aria-label="addAppliancesOwned"
                  >
                    <AddIcon fontSize="small" />
                    Magdagdag
                  </Button>
                </Box>
                <Box>
                  <Typography fontWeight="bold">
                    Bilang parte ng aking aplikasyon sa Kabahagi, ako ay
                    pumayag na ang mga datos ay beripikasyonin sa
                    pamamagitan ng pagbisata sa aming tahanan <span style={{color: 'red'}}>*</span>
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.agreement === 'Yes'}
                        onChange={() => setFieldValue('agreement', 'Yes')}
                      />
                    }
                    label="Yes"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.agreement === 'No'}
                        onChange={() => setFieldValue('agreement', 'No')}
                      />
                    }
                    label="No"
                  />
                  {errors.agreement && touched.agreement && (
                    <FormHelperText>{errors.agreement}</FormHelperText>
                  )}
                </Box>
              </Box>
            </Box>
          </Paper>
          <Box sx={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pt: 2,
            gap: 2,
            px: { xs: 1, sm: 0 }
          }}>
            <Button
              color="inherit"
              onClick={() => {
                saveSocialServiceInformation(values);
                handleBack();
              }}
              variant="outlined"
              sx={{ 
                width: { xs: 'auto', sm: 'auto' }
              }}
            >
              Back
            </Button>
            <Button 
              type="submit" 
              aria-label="Next" 
              disabled={isSubmitting}
              variant="contained"
              color="primary"
              sx={{ 
                width: { xs: 'auto', sm: 'auto' }
              }}
            >
              {isSubmitting ? 'Processing...' : 'Next'}
            </Button>
          </Box>
        </Form >
      )
      }
    </Formik >
  );
}

export default SocialService
